// GlobalStrategyNotifications.jsx
// src/components/GlobalStrategyNotifications.jsx
//
// FIX (v1.3):
//   - Entry fix: bulk_entry always fires notification regardless of isFirstSyncRef.
//     Previously the first-sync guard silently dropped entry notifications when
//     bulk_entry arrived before the first state_sync message.
//   - Exit fix: open → pending treated as valid exit transition (state_sync path)
//   - PnL display: uses ?? instead of || so zero PnL renders correctly
//   - Exit data: handles both flat payload (bulk_exit) and nested current_state (state_sync)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  Slide,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  CheckCircle as CheckCircleIcon,
  ExitToApp as ExitToAppIcon,
  AttachMoney as AttachMoneyIcon,
  TouchApp as TouchAppIcon,
} from '@mui/icons-material';

const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RECONNECT_ATTEMPTS = 10;
const WS_PING_INTERVAL = 30000;

// ============================================================================
// GLOBAL AUDIO UNLOCK MANAGER
// ============================================================================
const AudioUnlockManager = (() => {
  let audioContext = null;
  let isUnlocked = false;
  let unlockPromiseResolve = null;
  const unlockPromise = new Promise((resolve) => {
    unlockPromiseResolve = resolve;
  });

  const unlock = () => {
    if (isUnlocked) return;
    try {
      if (!audioContext) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioContext.state === 'suspended') {
        audioContext.resume().then(() => {
          isUnlocked = true;
          unlockPromiseResolve(true);
          console.log('[AUDIO-UNLOCK] AudioContext unlocked via user gesture');
        });
      } else {
        isUnlocked = true;
        unlockPromiseResolve(true);
        console.log('[AUDIO-UNLOCK] AudioContext already running');
      }
    } catch (err) {
      console.warn('[AUDIO-UNLOCK] AudioContext init failed:', err);
    }
    // Note: previous versions played a data: URI silent WAV here to prime
    // HTMLAudioElement on iOS Safari, but the WAV had data size 0 and Firefox
    // logs a decode error. AudioContext.resume() above covers all modern browsers.
  };

  if (typeof window !== 'undefined') {
    const events = ['click', 'touchstart', 'keydown'];
    const handler = () => {
      unlock();
      events.forEach(evt => document.removeEventListener(evt, handler, true));
    };
    events.forEach(evt => document.addEventListener(evt, handler, { once: false, capture: true }));
  }

  return {
    get isUnlocked() { return isUnlocked; },
    waitForUnlock: () => unlockPromise,
    unlock,
  };
})();

// ============================================================================
// AUDIO PLAYER HOOK
// ============================================================================
function useNotificationAudio(audioSrc, shouldPlay, isEnabled, debugLabel = '') {
  const audioRef = useRef(null);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const retryTimeoutRef = useRef(null);
  const wasPlayingRef = useRef(false);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioSrc);
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [audioSrc]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !shouldPlay || !isEnabled) {
      if (audio && wasPlayingRef.current) {
        audio.pause();
        audio.currentTime = 0;
        wasPlayingRef.current = false;
      }
      return;
    }
    if (wasPlayingRef.current && !audio.paused) return;

    const attemptPlay = () => {
      audio.loop = true;
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            setAudioBlocked(false);
            wasPlayingRef.current = true;
            console.log(`[AUDIO] Playing: ${debugLabel}`);
          })
          .catch((err) => {
            console.warn(`[AUDIO] Autoplay blocked for ${debugLabel}:`, err.name);
            setAudioBlocked(true);
            AudioUnlockManager.waitForUnlock().then(() => {
              if (shouldPlay && isEnabled) {
                retryTimeoutRef.current = setTimeout(() => {
                  audio.play()
                    .then(() => { setAudioBlocked(false); wasPlayingRef.current = true; })
                    .catch(() => {});
                }, 200);
              }
            });
          });
      }
    };
    attemptPlay();
    return () => {
      audio.pause();
      audio.currentTime = 0;
      wasPlayingRef.current = false;
    };
  }, [shouldPlay, isEnabled, debugLabel]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      wasPlayingRef.current = false;
    }
  }, []);

  const toggle = useCallback(() => { AudioUnlockManager.unlock(); }, []);

  return { audioBlocked, stop, toggle };
}

// ============================================================================
// AUDIO BLOCKED BADGE
// ============================================================================
const AudioBlockedBadge = ({ onClick }) => (
  <Chip
    icon={<TouchAppIcon sx={{ fontSize: 14 }} />}
    label="Tap to enable sound"
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      AudioUnlockManager.unlock();
      onClick?.();
    }}
    sx={{
      bgcolor: alpha('#f59e0b', 0.25),
      color: '#f59e0b',
      border: `1px solid ${alpha('#f59e0b', 0.5)}`,
      fontWeight: 600,
      cursor: 'pointer',
      animation: 'pulse-badge 1.5s ease-in-out infinite',
      '@keyframes pulse-badge': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.6 },
      },
    }}
  />
);

// ============================================================================
// HELPERS
// ============================================================================
const formatTime = (isoString) => {
  if (!isoString) return null;
  try {
    // Timestamps are already stored in ET by the backend webhook ingestion.
    // Extract components directly from the ISO string — no Date object, no
    // timezone conversion, otherwise the browser double-converts.
    const m = isoString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (m) {
      const [, year, month, day, hh, mm, ss] = m;
      let h = parseInt(hh, 10);
      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return months[parseInt(month,10) - 1] + ' ' + parseInt(day,10) + ', ' + year + ', ' + h12 + ':' + mm + ':' + ss + ' ' + period;
    }
    return isoString;
  } catch { return isoString; }
};

const calcDuration = (entryTime, exitTime) => {
  if (!entryTime || !exitTime) return null;
  try {
    let entry = entryTime;
    let exit = exitTime;
    if (!entry.includes('Z') && !entry.includes('+') && !entry.match(/-\d{2}:\d{2}$/)) entry += '-05:00';
    if (!exit.includes('Z') && !exit.includes('+') && !exit.match(/-\d{2}:\d{2}$/)) exit += '-05:00';
    const diffMs = new Date(exit) - new Date(entry);
    if (diffMs < 0) return null;
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  } catch { return null; }
};

// ============================================================================
// ENTRY NOTIFICATION COMPONENT
// ============================================================================
const EntryNotification = ({ notification, onClose }) => {
  const [audioEnabled, setAudioEnabled] = useState(true);
  const strategy = notification.strategy;

  const entryTime = strategy.current_state?.entry_time || strategy.entry_time;
  const entryPrice = strategy.current_state?.entry_price || strategy.entry_price;
  const triggerSignal = strategy.current_state?.trigger_signal || strategy.trigger_signal;
  const subStrategies = strategy.sub_strategies;
  const direction = strategy.direction || 'LONG';
  const isLong = direction.toUpperCase() === 'LONG';
  const directionColor = isLong ? '#00ff88' : '#ff4757';
  const DirectionIcon = isLong ? TrendingUpIcon : TrendingDownIcon;
  const strategyId = strategy.bulk_strategy_id || strategy.strategy_id || 'Unknown';

  console.log(`[ENTRY-NOTIFY] Rendering: ${strategyId} | price=${entryPrice} | time=${entryTime}`);

  const { audioBlocked, stop, toggle } = useNotificationAudio(
    '/new-notification-022-370046.mp3', true, audioEnabled, `ENTRY:${strategyId}`
  );

  const handleClose = () => { stop(); onClose(); };
  const handleToggleAudio = () => { toggle(); setAudioEnabled(prev => !prev); };

  return (
    <Alert
      severity="success"
      icon={<CheckCircleIcon sx={{ fontSize: 28 }} />}
      sx={{
        width: '100%',
        bgcolor: alpha('#1a1f2e', 0.98),
        border: `2px solid ${alpha(directionColor, 0.6)}`,
        borderRadius: 2,
        boxShadow: `0 8px 32px ${alpha(directionColor, 0.3)}`,
        '& .MuiAlert-icon': { color: directionColor },
      }}
      action={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={handleToggleAudio}
            sx={{
              color: audioEnabled ? alpha('#00ff88', 0.9) : alpha('#fff', 0.5),
              '&:hover': { color: audioEnabled ? '#00ff88' : '#fff', bgcolor: alpha('#fff', 0.1) },
            }}>
            {audioEnabled ? <VolumeUpIcon sx={{ fontSize: 20 }} /> : <VolumeOffIcon sx={{ fontSize: 20 }} />}
          </IconButton>
          <IconButton size="small" onClick={handleClose}
            sx={{ color: alpha('#fff', 0.7), '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.1) } }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      }
    >
      <AlertTitle sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>
        🎯 Strategy Complete!
      </AlertTitle>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, fontSize: '1rem' }}>{strategyId}</Typography>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Chip icon={<DirectionIcon sx={{ fontSize: 16 }} />} label={direction} size="small"
          sx={{ bgcolor: alpha(directionColor, 0.2), color: directionColor, border: `1px solid ${alpha(directionColor, 0.4)}`, fontWeight: 700, fontSize: '0.75rem' }} />
        <Chip label={strategy.symbol || 'NQ'} size="small"
          sx={{ bgcolor: alpha('#00d4ff', 0.2), color: '#00d4ff', border: `1px solid ${alpha('#00d4ff', 0.4)}`, fontWeight: 600, fontSize: '0.75rem' }} />
        <Chip label={strategy.timeframe || '1m'} size="small"
          sx={{ bgcolor: alpha('#764ba2', 0.2), color: '#764ba2', border: `1px solid ${alpha('#764ba2', 0.4)}`, fontWeight: 600, fontSize: '0.75rem' }} />
        <Chip label="100% COMPLETE" size="small"
          sx={{ bgcolor: alpha('#00ff88', 0.2), color: '#00ff88', border: `1px solid ${alpha('#00ff88', 0.4)}`, fontWeight: 700, fontSize: '0.75rem' }} />
        {audioBlocked && audioEnabled && <AudioBlockedBadge onClick={handleToggleAudio} />}
      </Stack>
      <Box sx={{ p: 1, bgcolor: alpha('#000', 0.3), borderRadius: 1, border: `1px solid ${alpha('#fff', 0.1)}` }}>
        <Stack spacing={0.5}>
          {entryPrice && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem' }}>
              <strong>Entry Price:</strong> ${entryPrice}
            </Typography>
          )}
          {entryTime && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem' }}>
              <strong>Entry Time:</strong> {formatTime(entryTime)}
            </Typography>
          )}
          {subStrategies && subStrategies.length > 0 && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem' }}>
              <strong>Sub-Strategies:</strong> {subStrategies.length} completed
            </Typography>
          )}
          {triggerSignal && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem' }}>
              <strong>Trigger:</strong>{' '}
              <Box component="span" sx={{ color: directionColor, fontWeight: 600 }}>{triggerSignal}</Box>
            </Typography>
          )}
        </Stack>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: alpha('#fff', 0.5), fontSize: '0.7rem', fontStyle: 'italic' }}>
        Click the speaker icon to toggle sound, or close to dismiss
      </Typography>
    </Alert>
  );
};

// ============================================================================
// EXIT NOTIFICATION COMPONENT
// ============================================================================
const ExitNotificationInline = ({ notification, onClose }) => {
  const [audioEnabled, setAudioEnabled] = useState(true);
  const strategy = notification.strategy;

  // Read from both flat payload fields (bulk_exit) and nested current_state (state_sync)
  const entryTime  = strategy.current_state?.entry_time  || strategy.entry_time;
  const exitTime   = strategy.current_state?.exit_time   || strategy.exit_time;
  const entryPrice = strategy.current_state?.entry_price ?? strategy.entry_price;
  const exitPrice  = strategy.current_state?.exit_price  ?? strategy.exit_price;
  const exitSignal = strategy.current_state?.exit_signal || strategy.exit_signal
    || strategy.configuration?.exit_signal?.name || strategy.exit_signal_name;
  const triggerSignal = strategy.current_state?.trigger_signal || strategy.trigger_signal;
  const subStrategies = strategy.sub_strategies;

  const direction = strategy.direction || 'LONG';
  const isLong = direction.toUpperCase() === 'LONG';
  const exitColor = '#ff4757';
  const DirectionIcon = isLong ? TrendingUpIcon : TrendingDownIcon;
  const strategyId = strategy.bulk_strategy_id || strategy.strategy_id || 'Unknown';

  // Use ?? so zero PnL renders correctly
  let pnl = strategy.current_state?.pnl ?? strategy.pnl;
  let pnlPercent = strategy.current_state?.pnl_pct ?? strategy.pnl_pct;
  if ((pnl === null || pnl === undefined) && entryPrice != null && exitPrice != null) {
    pnl = isLong ? exitPrice - entryPrice : entryPrice - exitPrice;
    pnlPercent = ((pnl / entryPrice) * 100).toFixed(2);
  }
  const isProfitable = pnl > 0;
  const pnlColor = isProfitable ? '#00ff88' : '#ff4757';
  const duration = calcDuration(entryTime, exitTime);

  console.log(`[EXIT-NOTIFY] Rendering: ${strategyId} | exit=${exitPrice} | pnl=${pnl}`);

  const { audioBlocked, stop, toggle } = useNotificationAudio(
    '/new-notification-022-370046.mp3', true, audioEnabled, `EXIT:${strategyId}`
  );

  const handleClose = () => { stop(); onClose(); };
  const handleToggleAudio = () => { toggle(); setAudioEnabled(prev => !prev); };

  return (
    <Alert
      severity="warning"
      icon={<ExitToAppIcon sx={{ fontSize: 28 }} />}
      sx={{
        width: '100%',
        bgcolor: alpha('#1a1f2e', 0.98),
        border: `2px solid ${alpha(exitColor, 0.6)}`,
        borderRadius: 2,
        boxShadow: `0 8px 32px ${alpha(exitColor, 0.3)}`,
        '& .MuiAlert-icon': { color: exitColor },
      }}
      action={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={handleToggleAudio}
            sx={{
              color: audioEnabled ? alpha('#00ff88', 0.9) : alpha('#fff', 0.5),
              '&:hover': { color: audioEnabled ? '#00ff88' : '#fff', bgcolor: alpha('#fff', 0.1) },
            }}>
            {audioEnabled ? <VolumeUpIcon sx={{ fontSize: 20 }} /> : <VolumeOffIcon sx={{ fontSize: 20 }} />}
          </IconButton>
          <IconButton size="small" onClick={handleClose}
            sx={{ color: alpha('#fff', 0.7), '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.1) } }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      }
    >
      <AlertTitle sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>
        🚨 Position Closed!
      </AlertTitle>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, fontSize: '1rem' }}>{strategyId}</Typography>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Chip icon={<ExitToAppIcon sx={{ fontSize: 16 }} />} label="EXIT" size="small"
          sx={{ bgcolor: alpha(exitColor, 0.2), color: exitColor, border: `1px solid ${alpha(exitColor, 0.4)}`, fontWeight: 700, fontSize: '0.75rem' }} />
        <Chip icon={<DirectionIcon sx={{ fontSize: 16 }} />} label={direction} size="small"
          sx={{ bgcolor: alpha(isLong ? '#00ff88' : '#ff4757', 0.2), color: isLong ? '#00ff88' : '#ff4757', fontWeight: 700, fontSize: '0.75rem' }} />
        <Chip label={strategy.symbol || 'NQ'} size="small"
          sx={{ bgcolor: alpha('#00d4ff', 0.2), color: '#00d4ff', fontWeight: 600, fontSize: '0.75rem' }} />
        {pnl !== null && pnl !== undefined && (
          <Chip
            icon={<AttachMoneyIcon sx={{ fontSize: 16 }} />}
            label={`${isProfitable ? '+' : ''}${typeof pnl === 'number' ? pnl.toFixed(2) : pnl}${pnlPercent != null ? ` (${isProfitable ? '+' : ''}${typeof pnlPercent === 'number' ? pnlPercent.toFixed(2) : pnlPercent}%)` : ''}`}
            size="small"
            sx={{ bgcolor: alpha(pnlColor, 0.2), color: pnlColor, fontWeight: 700, fontSize: '0.75rem' }}
          />
        )}
        {duration && (
          <Chip label={`⏱ ${duration}`} size="small"
            sx={{ bgcolor: alpha('#00d4ff', 0.2), color: '#00d4ff', fontWeight: 600, fontSize: '0.75rem' }} />
        )}
        {audioBlocked && audioEnabled && <AudioBlockedBadge onClick={handleToggleAudio} />}
      </Stack>
      <Box sx={{ p: 1, bgcolor: alpha('#000', 0.3), borderRadius: 1, border: `1px solid ${alpha('#fff', 0.1)}` }}>
        <Stack spacing={0.5}>
          {exitSignal && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem' }}>
              <strong>Exit Signal:</strong>{' '}
              <Box component="span" sx={{ color: exitColor, fontWeight: 600 }}>{exitSignal}</Box>
            </Typography>
          )}
          {entryPrice != null && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem' }}>
              <strong>Entry:</strong> ${entryPrice}
            </Typography>
          )}
          {exitPrice != null && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem' }}>
              <strong>Exit:</strong> ${exitPrice}
            </Typography>
          )}
          {entryTime && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem' }}>
              <strong>Entry Time:</strong> {formatTime(entryTime)}
            </Typography>
          )}
          {exitTime && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem' }}>
              <strong>Exit Time:</strong> {formatTime(exitTime)}
            </Typography>
          )}
          {duration && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem' }}>
              <strong>Duration:</strong>{' '}
              <Box component="span" sx={{ color: '#00d4ff', fontWeight: 600 }}>{duration}</Box>
            </Typography>
          )}
          {triggerSignal && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontSize: '0.75rem' }}>
              <strong>Entry Trigger:</strong>{' '}
              <Box component="span" sx={{ color: '#f59e0b', fontWeight: 600 }}>{triggerSignal}</Box>
            </Typography>
          )}
        </Stack>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: alpha('#fff', 0.5), fontSize: '0.7rem', fontStyle: 'italic' }}>
        Click the speaker icon to toggle sound, or close to dismiss
      </Typography>
    </Alert>
  );
};

// ============================================================================
// MAIN GLOBAL NOTIFICATIONS COMPONENT
// ============================================================================
const GlobalStrategyNotifications = ({ apiBaseUrl = window.location.origin }) => {
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const previousStatesRef = useRef({});
  const isFirstSyncRef = useRef(true);
  const lastDisconnectTimeRef = useRef(null);

  const handleDismiss = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Auto-dismiss after 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      setNotifications(prev => {
        const now = Date.now();
        return prev.filter(n => now - n.createdAt < 5 * 60 * 1000);
      });
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // ==================== CREATE ENTRY NOTIFICATION ====================
  const createEntryNotification = useCallback((strategyId, strategy) => {
    setNotifications(prev => {
      const exists = prev.find(n =>
        n.strategyId === strategyId && n.type === 'entry' && Date.now() - n.createdAt < 60000
      );
      if (exists) return prev;
      console.log(`[GLOBAL-NOTIFY] ENTRY NOTIFICATION: ${strategyId}`);
      return [...prev, {
        id: `entry-${strategyId}-${Date.now()}`,
        strategyId,
        type: 'entry',
        strategy: { ...strategy, bulk_strategy_id: strategyId },
        createdAt: Date.now(),
      }];
    });
  }, []);

  // ==================== CREATE EXIT NOTIFICATION ====================
  const createExitNotification = useCallback((strategyId, strategy) => {
    const previousState = previousStatesRef.current[strategyId] || {};
    const storedPositionData = previousState.positionData || {};

    // Merge stored entry data with incoming exit data.
    // bulk_exit sends flat fields; state_sync sends nested current_state.
    const mergedCurrentState = {
      ...(storedPositionData.current_state || {}),
      ...(strategy.current_state || {}),
      ...(strategy.exit_time   != null ? { exit_time:   strategy.exit_time   } : {}),
      ...(strategy.exit_price  != null ? { exit_price:  strategy.exit_price  } : {}),
      ...(strategy.pnl         != null ? { pnl:         strategy.pnl         } : {}),
      ...(strategy.pnl_pct     != null ? { pnl_pct:     strategy.pnl_pct     } : {}),
      ...(strategy.entry_time  != null ? { entry_time:  strategy.entry_time  } : {}),
      ...(strategy.entry_price != null ? { entry_price: strategy.entry_price } : {}),
    };

    const mergedStrategy = {
      ...storedPositionData,
      ...strategy,
      bulk_strategy_id: strategyId,
      current_state: mergedCurrentState,
    };

    console.log(`[GLOBAL-NOTIFY] Exit data for ${strategyId}:`, {
      entryPrice: mergedCurrentState.entry_price,
      exitPrice:  mergedCurrentState.exit_price,
      pnl:        mergedCurrentState.pnl,
      entryTime:  mergedCurrentState.entry_time,
      exitTime:   mergedCurrentState.exit_time,
    });

    setNotifications(prev => {
      const exists = prev.find(n =>
        n.strategyId === strategyId && n.type === 'exit' && Date.now() - n.createdAt < 60000
      );
      if (exists) return prev;
      const withoutEntry = prev.filter(n => !(n.strategyId === strategyId && n.type === 'entry'));
      console.log(`[GLOBAL-NOTIFY] EXIT NOTIFICATION: ${strategyId}`);
      return [...withoutEntry, {
        id: `exit-${strategyId}-${Date.now()}`,
        strategyId,
        type: 'exit',
        strategy: mergedStrategy,
        createdAt: Date.now(),
      }];
    });
  }, []);

  // ==================== AUTHORITATIVE MESSAGES (bulk_entry / bulk_exit) ====================
  const handleAuthoritativeMessage = useCallback((messageType, data) => {
    const strategyId = data.strategy_id || data.bulk_strategy_id;
    if (!strategyId) return;
    const strategy = data.data || data;

    if (messageType === 'bulk_entry') {
      // FIX: Never skip bulk_entry regardless of isFirstSyncRef.
      // bulk_entry is always a real-time event. The previous first-sync guard
      // was silently dropping entry notifications when bulk_entry arrived before
      // the first state_sync — common when the launcher runs right after page load.
      console.log(`[GLOBAL-NOTIFY] AUTHORITATIVE bulk_entry for ${strategyId}`);
      createEntryNotification(strategyId, strategy);
      previousStatesRef.current[strategyId] = {
        status: 'open',
        percentage: 100,
        positionData: { ...strategy, bulk_strategy_id: strategyId },
        positionOpenedAt: Date.now(),
      };
      return;
    }

    if (messageType === 'bulk_exit') {
      console.log(`[GLOBAL-NOTIFY] AUTHORITATIVE bulk_exit for ${strategyId}`);

      // Skip on first sync — page-load catch-up, not a live event
      if (isFirstSyncRef.current) {
        console.log(`[GLOBAL-NOTIFY] Skipping bulk_exit during first sync: ${strategyId}`);
        previousStatesRef.current[strategyId] = { status: 'completed', percentage: 0 };
        return;
      }

      // 10s minimum guard against stale exits
      const previousState = previousStatesRef.current[strategyId] || {};
      const positionOpenedAt = previousState.positionOpenedAt;
      if (positionOpenedAt && (Date.now() - positionOpenedAt) < 10000) {
        console.log(`[GLOBAL-NOTIFY] Exit skipped — position open < 10s: ${strategyId}`);
        previousStatesRef.current[strategyId] = { status: 'completed', percentage: 0 };
        return;
      }

      createExitNotification(strategyId, strategy);
      previousStatesRef.current[strategyId] = { status: 'completed', percentage: 0 };
    }
  }, [createEntryNotification, createExitNotification]);

  // ==================== DELTA-BASED (state_sync reconnect catch-up) ====================
  const processStateSyncStrategies = useCallback((strategies, isReconnectSync = false) => {
    if (!strategies || !Array.isArray(strategies)) return;

    strategies.forEach(strategy => {
      const strategyId = strategy.strategy_id || strategy.bulk_strategy_id;
      if (!strategyId) return;

      const currentStatus   = strategy.status || strategy.current_state?.status || 'idle';
      const currentProgress = strategy.completion_percentage ?? strategy.current_state?.completion_percentage ?? 0;
      const previousState   = previousStatesRef.current[strategyId] || { status: 'idle', percentage: 0 };
      const previousStatus  = previousState.status;

      // On first sync: record state only, no notifications
      if (isFirstSyncRef.current) {
        const initState = { status: currentStatus, percentage: currentProgress };
        if (currentStatus === 'open') {
          initState.positionData = { ...strategy };
          initState.positionOpenedAt = Date.now();
        }
        previousStatesRef.current[strategyId] = initState;
        return;
      }

      // Entry detection
      const justOpenedPosition = currentStatus === 'open' && previousStatus !== 'open';
      if (justOpenedPosition || (isReconnectSync && currentStatus === 'open' && previousStatus !== 'open')) {
        const reason = isReconnectSync ? '(reconnect)' : '(state_sync delta)';
        console.log(`[GLOBAL-NOTIFY] ENTRY via state_sync: ${strategyId} ${reason}`);
        createEntryNotification(strategyId, strategy);
      }

      // Exit detection — open → ANY non-open state (including pending reset after trade)
      const justExited = previousStatus === 'open' && currentStatus !== 'open';
      if (justExited) {
        const positionOpenedAt = previousState.positionOpenedAt;
        if (positionOpenedAt && (Date.now() - positionOpenedAt) < 10000) {
          // Skip — too quick, likely a timing artifact
        } else {
          const reason = isReconnectSync ? '(reconnect)' : '(state_sync delta)';
          console.log(`[GLOBAL-NOTIFY] EXIT via state_sync: ${strategyId} ${reason}`);
          createExitNotification(strategyId, strategy);
        }
      }

      // Update tracking
      const updatedState = { status: currentStatus, percentage: currentProgress };
      if (currentStatus === 'open') {
        updatedState.positionData = { ...strategy };
        updatedState.positionOpenedAt = previousState.positionOpenedAt || Date.now();
      } else if (previousState.positionData) {
        updatedState.positionData = previousState.positionData;
      }
      previousStatesRef.current[strategyId] = updatedState;
    });
  }, [createEntryNotification, createExitNotification]);

  // ==================== WEBSOCKET MESSAGE HANDLER ====================
  const handleWebSocketMessage = useCallback((data) => {
    const messageType = data.type || 'unknown';
    if (messageType === 'connection' || messageType === 'pong') return;

    // Authoritative real-time events
    if (messageType === 'bulk_entry' || messageType === 'bulk_exit') {
      handleAuthoritativeMessage(messageType, data);
      const strategyId = data.strategy_id || data.bulk_strategy_id;
      if (strategyId) {
        const strategy = data.data || data;
        if (messageType === 'bulk_entry') {
          previousStatesRef.current[strategyId] = {
            status: 'open',
            percentage: 100,
            positionData: { ...strategy, bulk_strategy_id: strategyId },
            positionOpenedAt: previousStatesRef.current[strategyId]?.positionOpenedAt || Date.now(),
          };
        } else {
          previousStatesRef.current[strategyId] = {
            status: strategy.status || strategy.current_state?.status || 'completed',
            percentage: 0,
          };
        }
      }
      return;
    }

    // Delta-based catch-up
    if (messageType === 'state_sync') {
      const activeStrategies = data.data?.active_strategies || data.active_strategies || [];
      const isReconnectSync = lastDisconnectTimeRef.current !== null && !isFirstSyncRef.current;
      if (activeStrategies.length > 0) {
        processStateSyncStrategies(activeStrategies, isReconnectSync);
      }
      if (isFirstSyncRef.current) {
        isFirstSyncRef.current = false;
        console.log(`[GLOBAL-NOTIFY] Initial state recorded for ${activeStrategies.length} strategies. Notifications active.`);
      }
      lastDisconnectTimeRef.current = null;
      return;
    }

    // Non-authoritative — update tracking state only, no notifications
    if (messageType === 'strategy_update' || messageType === 'pending_update') {
      const strategyData = data.data || data;
      const strategyId = strategyData.strategy_id || strategyData.bulk_strategy_id;
      if (!strategyId) return;
      const currentStatus   = strategyData.status || strategyData.current_state?.status;
      const currentProgress = strategyData.completion_percentage ?? strategyData.current_state?.completion_percentage;
      const previousState   = previousStatesRef.current[strategyId] || {};
      const updatedState    = { ...previousState };
      if (currentStatus) updatedState.status = currentStatus;
      if (currentProgress != null) updatedState.percentage = currentProgress;
      if (currentStatus === 'open') {
        updatedState.positionData = { ...strategyData, bulk_strategy_id: strategyId };
        updatedState.positionOpenedAt = previousState.positionOpenedAt || Date.now();
      }
      previousStatesRef.current[strategyId] = updatedState;
    }
  }, [handleAuthoritativeMessage, processStateSyncStrategies]);

  // ==================== WEBSOCKET CONNECTION ====================
  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }

    try {
      // Use same-origin WebSocket so the nginx proxy handles routing to the
      // backend. This avoids cross-origin connect failures when apiBaseUrl
      // points to a remote domain (e.g. dev at localhost:2000 with backend
      // behind nginx, or when REACT_APP_API_URL=https://ojuimo.com is set).
      const isSecure = window.location.protocol === 'https:';
      const wsHost = window.location.host;
      const wsUrl = `${isSecure ? 'wss:' : 'ws:'}//${wsHost}/ws/strategy-alerts/`;
      console.log(`[GLOBAL-WS] Connecting to: ${wsUrl}`);

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (!mountedRef.current) return;
        console.log('[GLOBAL-WS] Connected');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          handleWebSocketMessage(JSON.parse(event.data));
        } catch (err) {
          console.error('[GLOBAL-WS] Parse error:', err);
        }
      };

      ws.onerror = (error) => console.error('[GLOBAL-WS] Error:', error);

      ws.onclose = (event) => {
        if (!mountedRef.current) return;
        console.log(`[GLOBAL-WS] Disconnected (code: ${event.code})`);
        setIsConnected(false);
        lastDisconnectTimeRef.current = Date.now();
        if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }
        if (event.code !== 1000 && reconnectAttemptsRef.current < WS_MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current++;
          const delay = WS_RECONNECT_DELAY * Math.min(reconnectAttemptsRef.current, 5);
          console.log(`[GLOBAL-WS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
          reconnectTimeoutRef.current = setTimeout(() => { if (mountedRef.current) connect(); }, delay);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('[GLOBAL-WS] Connection error:', err);
    }
  }, [apiBaseUrl, handleWebSocketMessage]);

  useEffect(() => {
    mountedRef.current = true;
    isFirstSyncRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (wsRef.current) { wsRef.current.close(1000, 'Component unmounting'); wsRef.current = null; }
    };
  }, [connect]);

  // ==================== RENDER ====================
  return (
    <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, maxWidth: 420 }}>
      <Stack spacing={2}>
        {notifications.map((notification, index) => (
          <Slide key={notification.id} direction="left" in={true} mountOnEnter unmountOnExit
            style={{ transitionDelay: `${index * 100}ms` }}>
            <Box>
              {notification.type === 'entry' ? (
                <EntryNotification
                  notification={notification}
                  onClose={() => handleDismiss(notification.id)}
                />
              ) : (
                <ExitNotificationInline
                  notification={notification}
                  onClose={() => handleDismiss(notification.id)}
                />
              )}
            </Box>
          </Slide>
        ))}
      </Stack>
    </Box>
  );
};

export default GlobalStrategyNotifications;
